import type { IconName } from './icons';
import { SITE } from './site';

/**
 * 「Agent 接入」页内容。
 *
 * 这一页对外的承诺只有两类：站内有哪些机器可读地址，以及检索接口怎么调。
 * 地址一律由 `SITE.url` 拼出，改域名不需要改这里的文案。
 */

const base = SITE.url.replace(/\/+$/, '');

export const AGENTS_INTRO = {
  eyebrow: 'Agent 接入',
  title: '让 Agent 读到准确的一手内容',
  lead:
    '本站页面由浏览器渲染，仅抓取 HTML 只能得到空壳。为此提供两条通道：一条用于阅读，把每一页转成纯文本；一条用于检索，按关键词定位到具体条目。两者都与页面共用同一份内容，不另存副本。',
} as const;

export interface AgentEntry {
  id: string;
  icon: IconName;
  label: string;
  title: string;
  summary: string;
  points: readonly string[];
  /** 该入口的入口地址，用于展示与复制。 */
  address: string;
  addressLabel: string;
}

export const AGENT_ENTRIES: readonly AgentEntry[] = [
  {
    id: 'reading',
    icon: 'audit',
    label: 'Agent 阅读',
    title: '整页转成 Markdown',
    summary: '适合需要完整上下文的场景：把某一页的全部正文一次性取回，不执行 JavaScript。',
    points: [
      '每页都有对应的 .md 地址，与页面路径同名，例如 /platform 对应 /platform.md。',
      '请求页面地址并带上 Accept: text/markdown，同样返回 Markdown，无需另记地址。',
      '/llms.txt 是目录，列出全部页面与文章；/llms-full.txt 是全文拼接，供一次性摄入。',
    ],
    address: `${base}/llms.txt`,
    addressLabel: 'llms.txt',
  },
  {
    id: 'retrieval',
    icon: 'search',
    label: 'Agent 检索',
    title: '按关键词定位条目',
    summary: '适合不知道去哪一页的场景：先检索，再按返回的 markdownUrl 取正文。',
    points: [
      '接口接受中文或英文关键词，标题命中的条目排在前面。',
      '每条结果带 url、markdownUrl 与一句话描述，拿到即可决定是否继续读取。',
      '索引是静态文件 /agent/index.json，需要离线处理时可整份取走。',
    ],
    address: `${base}/api/agent/search?q=遥测`,
    addressLabel: '检索示例',
  },
];

export interface AgentFile {
  path: string;
  note: string;
}

export const AGENT_FILES: readonly AgentFile[] = [
  { path: '/llms.txt', note: '目录：站点简介、全部页面与文章、检索接口说明' },
  { path: '/llms-full.txt', note: '全文：所有页面的 Markdown 按顺序拼接' },
  { path: '/agent/index.json', note: '索引：页面、产品与文章的条目数组，供检索与离线处理' },
  { path: '/{path}.md', note: '单页 Markdown，如 /products.md、/news/{slug}.md' },
  { path: '/api/agent/search', note: '检索接口，参数 q、limit、kind' },
];

export interface AgentParam {
  name: string;
  required: string;
  note: string;
}

export const AGENT_PARAMS: readonly AgentParam[] = [
  { name: 'q', required: '必填', note: '关键词，多个词以空格分隔，需全部命中；长度上限 200 字符' },
  { name: 'limit', required: '可选', note: '返回条数，范围 1–20，默认 8' },
  { name: 'kind', required: '可选', note: '限定条目类型：page、product 或 news' },
];

export const AGENT_EXAMPLES: readonly { title: string; code: string }[] = [
  {
    title: '检索与读取',
    code: `curl -s '${base}/api/agent/search?q=告警阈值&limit=3'
curl -s '${base}/platform.md'`,
  },
  {
    title: '按内容类型协商',
    code: `curl -s -H 'Accept: text/markdown' '${base}/solutions'`,
  },
];

export const AGENT_RULES: readonly string[] = [
  '允许抓取、缓存与引用本站内容，引用时请保留来源链接，便于读者核对原文。',
  '页面内容随站点构建更新；索引里的 updated 字段标明生成日期，请以线上版本为准。',
  '本站不提供报价数字，方案差异见「方案与定价」页；价格依机体数量与部署方式报价。',
  '本站不销售无人机整机，也不提供整机购买渠道。',
  '接口为只读，不需要鉴权，也不接受写入。批量抓取请控制在合理频率，建议并发不超过 4。',
];

export const AGENT_NOTE =
  '生成产物在每次构建时重新生成，与页面同源同版本。若发现某页的 Markdown 与页面不一致，通常是缓存所致，可加时间戳重试。';
